import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTeamStore } from '../store/teamStore';
import type { Player } from '../types/index';
import { UserPlus, Upload, Download, Users, Activity, Printer } from 'lucide-react';
import { piiService } from '../services/piiService';
import { playerCardService } from '../services/playerCardService';
import { toast } from 'react-hot-toast';
import PageHeader from './layout/PageHeader';
import { Link } from 'react-router-dom';
import { PlayerAvailabilityComponent } from './PlayerAvailabilityComponent';
import { PlayerCardList } from './player-cards/PlayerCardList';
import { printRoster } from '../utils/rosterPrintUtils';
import type { PlayerCard } from '../types/playerCard';
import { getLastLevelPlayedOptions } from '../types/playerCard';

// Add useMediaQuery hook
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
};

const RosterManagement: React.FC = () => {
  const { currentTeam, allTeams, fetchAllTeams } = useTeamStore();
  const [activeTab, setActiveTab] = useState<'roster' | 'availability'>('roster');
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add mobile detection
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Load teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      await fetchAllTeams();
      setIsLoadingTeams(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  const fetchPlayers = async () => {
    try {
      console.log(`[PlayerManagement] Fetching players for team: ${currentTeam?.name} (${currentTeam?.id})`);
      
      const { data, error } = await supabase
        .from('players')
        .select(`
          id,
          first_name,
          last_name,
          number,
          position,
          height,
          weight,
          speed,
          strength,
          experience,
          team_id,
          play_participation,
          injury_status,
          is_sub
        `)
        .eq('team_id', currentTeam?.id)
        .order('last_name');

      if (error) throw error;
      
      console.log(`[PlayerManagement] Fetched ${data?.length || 0} players`);
      // Debug check - verify player team IDs match current team
      if (data && data.length > 0 && data[0].team_id !== currentTeam?.id) {
        console.error(`[PlayerManagement] MISMATCH: Players are for team ${data[0].team_id}, but current team is ${currentTeam?.id}`);
      }
      
      // Debug MPR data and map fields
      const mappedPlayers = data?.map(player => {
        console.log(`[PlayerManagement] Raw player data for ${player.first_name} ${player.last_name}:`, {
          play_participation: player.play_participation
        });
        
        return {
          ...player,
          playParticipation: player.play_participation
        };
      }) || [];
      
      if (mappedPlayers.length > 0) {
        mappedPlayers.forEach(player => {
          if (player.playParticipation) {
            console.log(`[PlayerManagement] Mapped MPR data for ${player.first_name} ${player.last_name}:`, player.playParticipation);
          }
        });
      }
      
      setPlayers(mappedPlayers);
    } catch (err) {
      console.error('Error fetching players:', err);
      toast.error('Failed to load players');
    }
  };

  useEffect(() => {
    if (currentTeam) {
      fetchPlayers();
    }
  }, [currentTeam]);

  // Add team change event listener
  useEffect(() => {
    // Track the last team ID to avoid duplicate fetches
    let lastTeamId = currentTeam?.id;
    
    // Function to handle team changes
    const handleTeamChange = (event: CustomEvent) => {
      console.log(`[PlayerManagement] Received team-changed event: ${event.detail.name} (${event.detail.teamId})`);
      
      // Only reload if the team actually changed
      if (event.detail.teamId && event.detail.teamId !== lastTeamId) {
        console.log('[PlayerManagement] Team changed, refreshing players');
        lastTeamId = event.detail.teamId;
        // Reset players and refetch
        setPlayers([]);
        fetchPlayers();
      }
    };
    
    // Listen for team changes
    window.addEventListener('team-changed', handleTeamChange as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('team-changed', handleTeamChange as EventListener);
    };
  }, [currentTeam]);

  const handlePlayerUpdate = (playerId: string, updates: Partial<Player>) => {
    setPlayers(prevPlayers => 
      prevPlayers.map(player => 
        player.id === playerId 
          ? { ...player, ...updates }
          : player
      )
    );
  };

  // Print roster handler
  const handlePrint = useCallback(async () => {
    if (!currentTeam) {
      toast.error('No team selected');
      return;
    }

    try {
      // Fetch player cards with player data
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      
      // Also fetch players with play_participation for MPR
      const { data: players } = await supabase
        .from('players')
        .select('*, play_participation')
        .eq('team_id', currentTeam.id);

      // Map players to cards
      const cardsWithPlayers: PlayerCard[] = cards.map((card) => {
        const player = players?.find(p => p.id === card.player_id);
        const playerWithParticipation = player ? {
          ...player,
          playParticipation: player.play_participation
        } : undefined;
        return { ...card, player: playerWithParticipation };
      });

      // Print the roster
      await printRoster(currentTeam, cardsWithPlayers);
    } catch (error) {
      console.error('Error printing roster:', error);
      toast.error('Failed to print roster');
    }
  }, [currentTeam]);

  const generateCsvTemplate = () => {
    // Updated headers to match player cards structure
    // position_groups: comma-separated list of position group keys (e.g., "QB,RB,WR")
    // last_level_played: Baseball/Softball: Tee-Ball | Rec-League | Competitive Rec | Travel-A | Travel-AA | Travel-AAA | Majors/Elite; other sports: Rec League | Travel League
    // has_mpr: true/false
    // minimum_plays: number (only if has_mpr is true)
    const headers = "first_name,last_name,position_groups,number,height,weight,last_level_played,nickname,phone,email,has_mpr,minimum_plays";
    const sampleData = [
      "Steve,Roughen,\"DL,EDGE\",51,\"6'1\"\"\",210,Rec-League,,(555) 553-3196,steve.roughen@example.com,false,",
      "Axel,Gridiron,TE,76,\"5'8\"\"\",121,Travel-A,,(555) 501-7916,axel.gridiron@example.com,false,",
      "Jax,Snapper,OL,59,\"6'1\"\"\",240,Rec-League,,(555) 961-6625,jax.snapper@example.com,false,",
      "Ace,Roughen,\"DL,LB\",66,\"6'0\"\"\",183,Competitive Rec,,(555) 479-5029,ace.roughen@example.com,true,8",
      "Ben,Blitzer,DB,85,\"5'1\"\"\",83,Tee-Ball,,(555) 884-2364,ben.blitzer@example.com,false,",
      "Jack,Downs,LB,30,\"5'5\"\"\",124,Rec-League,,(555) 487-3638,jack.downs@example.com,true,10",
      "Hank,Flexman,DB,80,\"5'10\"\"\",107,Travel-AA,,(555) 526-6008,hank.flexman@example.com,false,",
      "Frank,Sackwell,LB,66,\"5'11\"\"\",156,Rec-League,,(555) 447-4586,frank.sackwell@example.com,false,",
      "Tex,Touchdown,TE,38,\"5'2\"\"\",115,Tee-Ball,,(555) 553-8960,tex.touchdown@example.com,true,6",
      "Tank,Yardley,RB,19,\"5'4\"\"\",110,Travel-AAA,\"The Tank\",(555) 655-5274,tank.yardley@example.com,false,",
      "Slade,Haymaker,LB,75,\"5'8\"\"\",147,Competitive Rec,,(555) 389-8141,slade.haymaker@example.com,false,",
      "Blaze,Cannon,K,39,\"5'4\"\"\",116,Rec-League,,(555) 472-2025,blaze.cannon@example.com,false,",
      "Sam,Sackwell,RB,35,\"5'8\"\"\",120,Tee-Ball,,(555) 940-7218,sam.sackwell@example.com,false,",
      "Mike,Gapper,DL,40,\"5'6\"\"\",216,Tee-Ball,,(555) 493-9538,mike.gapper@example.com,true,8",
      "Chad,Fumbleton,WR,68,\"6'0\"\"\",240,Rec-League,,(555) 525-7156,chad.fumbleton@example.com,false,",
      "Bo,Dimes,QB,24,\"5'4\"\"\",129,Rec-League,,(555) 255-6793,bo.dimes@example.com,false,",
      "Brick,Crankshaft,TE,92,\"5'8\"\"\",152,Tee-Ball,,(555) 106-9973,brick.crankshaft@example.com,true,6",
      "Zeke,Slammer,DB,20,\"5'1\"\"\",107,Tee-Ball,,(555) 891-7269,zeke.slammer@example.com,false,",
      "Jet,Straps,DB,65,\"5'6\"\"\",131,Tee-Ball,,(555) 906-5311,jet.straps@example.com,true,8",
      "Buck,Turfburn,OL,68,\"5'8\"\"\",167,Majors/Elite,,(555) 409-3188,buck.turfburn@example.com,false,",
      "Dash,Crushman,OL,72,\"5'7\"\"\",211,Tee-Ball,,(555) 506-2279,dash.crushman@example.com,false,",
      "Mack,Johnson,OL,9,\"6'1\"\"\",177,Tee-Ball,,(555) 641-4508,mack.johnson@example.com,false,",
      "Jace,Helmetson,WR,44,\"5'7\"\"\",117,Travel-A,,(555) 757-7894,jace.helmetson@example.com,false,"
    ].join('\n');
    
    const csvContent = `${headers}\n${sampleData}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'player_cards_roster_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 0) return [];
    
    const headers = parseCSVLine(lines[0]);
    
    return lines.slice(1).map(line => {
      const values = parseCSVLine(line);
      const obj: Record<string, string> = {};
      
      headers.forEach((header, i) => {
        obj[header.trim()] = values[i]?.trim() || '';
      });
      
      return obj;
    });
  };

  // Helper function to properly parse CSV lines with quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Handle escaped quotes
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add the last field
    result.push(current);
    
    return result;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsImporting(true);
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const records = parseCSV(text);
        
        if (!records.length) {
          toast.error('No valid records found in the CSV file');
          setIsImporting(false);
          return;
        }
        
        let successCount = 0;
        let errorCount = 0;
        
        for (const record of records) {
          try {
            // Skip completely empty rows
            const hasAnyData = Object.values(record).some(value => value.trim() !== '');
            if (!hasAnyData) {
              continue; // Skip empty rows without counting as error
            }
            
            // Validate required fields
            if (!record.first_name?.trim()) {
              console.log('Skipping row - missing first_name:', record);
              errorCount++;
              continue;
            }
            
            // Parse position groups (comma-separated list)
            const positionGroupsStr = record.position_groups?.trim() || '';
            const positionGroups = positionGroupsStr 
              ? positionGroupsStr.split(',').map(p => p.trim().toUpperCase()).filter(p => p)
              : [];
            
            // Parse and validate numeric fields
            const parseNumber = (value: string, defaultValue: number, min?: number, max?: number): number => {
              if (!value || value.trim() === '') return defaultValue;
              const parsed = parseInt(value);
              if (isNaN(parsed)) return defaultValue;
              if (min !== undefined && parsed < min) return defaultValue;
              if (max !== undefined && parsed > max) return defaultValue;
              return parsed;
            };
            
            // Parse last level played (sport-specific: baseball/softball full list, other sports Rec League / Travel League)
            const lastLevelAllowedValues = getLastLevelPlayedOptions(currentTeam?.sport);
            const lastLevelPlayedRaw = record.last_level_played?.trim() || '';
            const lastLevelPlayed = lastLevelAllowedValues.includes(lastLevelPlayedRaw) ? lastLevelPlayedRaw : undefined;
            
            // Parse MPR data
            const hasMPR = record.has_mpr?.toLowerCase().trim() === 'true' || record.has_mpr?.toLowerCase().trim() === 'yes';
            const minimumPlays = hasMPR ? parseNumber(record.minimum_plays, 0, 0, 100) : 0;
            
            // Add player to database
            const { data, error } = await supabase
              .from('players')
              .insert({
                team_id: currentTeam!.id,
                first_name: record.first_name.trim(),
                last_name: record.last_name?.trim() || '',
                position: positionGroups[0] || '--', // Use first position group as legacy position
                number: parseNumber(record.number, 0, 0, 999),
                height: record.height?.trim() || '',
                weight: parseNumber(record.weight, 0, 0, 500),
                speed: 5, // Default values
                strength: 5,
                experience: 'Beginner'
              })
              .select();
            
            if (error) throw error;
            
            if (data && data.length > 0) {
              const playerId = data[0].id;
              
              // Add PII data if available
              const phone = record.phone?.trim() || '';
              const email = record.email?.trim() || '';
              
              if (phone || email) {
                await piiService.updatePlayerPII(playerId, phone, email);
              }
              
              // Create player card with imported data
              const cardData: any = {
                eligible_positions: positionGroups,
                last_level_played: lastLevelPlayed || undefined,
                nickname: record.nickname?.trim() || undefined,
              };
              
              // Determine role types for football based on position groups
              if (currentTeam?.sport === 'Football') {
                const offensiveGroups = ['QB', 'RB', 'WR', 'TE', 'OL'];
                const defensiveGroups = ['DL', 'EDGE', 'LB', 'DB'];
                const hasOffensive = positionGroups.some(p => offensiveGroups.includes(p));
                const hasDefensive = positionGroups.some(p => defensiveGroups.includes(p));
                
                cardData.can_play_line = positionGroups.some(p => ['OL', 'DL', 'EDGE'].includes(p));
                cardData.can_play_skill = positionGroups.some(p => ['QB', 'RB', 'WR', 'TE', 'LB', 'DB'].includes(p));
                cardData.offense_only = hasOffensive && !hasDefensive;
                cardData.defense_only = hasDefensive && !hasOffensive;
                
                // Set role types
                const roleTypes: string[] = [];
                if (cardData.can_play_line) roleTypes.push('line');
                if (cardData.can_play_skill) roleTypes.push('skill');
                cardData.role_types = roleTypes;
              }
              
              await playerCardService.getOrCreatePlayerCard(playerId, currentTeam!.id);
              await playerCardService.updatePlayerCard(
                (await playerCardService.getPlayerCardByPlayer(playerId, currentTeam!.id))!.id,
                cardData
              );
              
              // Set MPR data on player
              if (hasMPR) {
                const playParticipation = {
                  hasMPR: true,
                  minimumRequiredPlays: minimumPlays,
                  priorityLevel: 2,
                  playsLogged: 0,
                  forceInsert: false,
                  isAvailable: true,
                };
                
                await supabase
                  .from('players')
                  .update({ play_participation: playParticipation })
                  .eq('id', playerId);
              }
            }
            
            successCount++;
          } catch (err) {
            console.error('Error importing player:', err);
            console.error('Failed record:', record);
            
            // Log specific error details for debugging
            if (err instanceof Error) {
              console.error('Error message:', err.message);
            }
            
            errorCount++;
          }
        }
        
        // Refresh will happen automatically when navigating to player cards
        
        // Show results
        const totalProcessed = successCount + errorCount;
        console.log(`[CSV Import] Processed ${totalProcessed} records: ${successCount} successful, ${errorCount} failed`);
        
        if (successCount > 0 && errorCount === 0) {
          toast.success(`Successfully imported all ${successCount} players`);
        } else if (successCount > 0 && errorCount > 0) {
          toast.success(`Imported ${successCount} players successfully`);
          toast.error(`Failed to import ${errorCount} players (check console for details)`);
        } else if (errorCount > 0) {
          toast.error(`Failed to import ${errorCount} players. Check that required fields (first_name) are filled.`);
        } else {
          toast.error('No valid player records found in the CSV file');
        }
        
        setIsImporting(false);
      };
      
      reader.readAsText(file);
    } catch (err) {
      console.error('Error importing CSV:', err);
      toast.error('Failed to import CSV file');
      setIsImporting(false);
    }
  };

  // Show loading state while teams are being loaded
  if (isLoadingTeams) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  // Show team setup message only after teams are loaded and there are no teams
  if (allTeams.length === 0) {
    return (
      <div className="text-center">
        <h2 className="text-h3 mb-4">No Teams Found</h2>
        <p className="mb-6 caption">Get started by setting up your team.</p>
        <Link
          to="/team-setup"
          className="btn-primary"
        >
          Set Up Team
        </Link>
      </div>
    );
  }

  // Show loading state for team data
  if (!currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading team data...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {/* Page Header */}
      <PageHeader 
        title="Roster" 
        icon={<Users className="h-6 w-6" />}
        actions={
          <button
            onClick={handlePrint}
            className="btn-secondary flex items-center gap-1 px-2 py-1 text-sm"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        }
      />
      
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="inline-flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('roster')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'roster'
                ? 'bg-white shadow text-primary'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Roster</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'availability'
                ? 'bg-white shadow text-primary'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              <span>Player Availability</span>
            </div>
          </button>
        </div>
      </div>

      {/* Action Buttons - Only show for roster tab */}
      {activeTab === 'roster' && (
        <div className="mb-6 flex gap-2 flex-wrap">
          <button
            onClick={generateCsvTemplate}
            className="btn-secondary flex items-center gap-1 px-2 py-1.5 text-sm"
          >
            <Download className="w-4 h-4" />
            {isMobile ? 'Sample' : 'Download Sample'}
          </button>
          <div className="relative">
            <input
              type="file"
              accept=".csv,text/csv"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              capture="environment"
            />
            <button
              onClick={handleFileSelect}
              className="btn-secondary flex items-center gap-1 px-2 py-1.5 text-sm relative z-10"
              disabled={isImporting}
            >
              <Upload className="w-4 h-4" />
              {isImporting ? 'Importing...' : isMobile ? 'Import' : 'Import CSV'}
            </button>
          </div>
          <Link
            to="/player-cards/new"
            className="btn-primary flex items-center gap-1 px-2 py-1.5 text-sm"
          >
            <UserPlus className="w-4 h-4" />
            {'Create Player'}
          </Link>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'roster' ? (
        /* Player Cards Integration */
        <div>
          <PlayerCardList />
        </div>
      ) : (
        /* Player Availability Tab Content */
        <div className="bg-white rounded-lg shadow p-6">
          <PlayerAvailabilityComponent
            players={players}
            teamId={currentTeam.id}
            onPlayerUpdate={handlePlayerUpdate}
            onInjuryStatusChange={() => {
              // Trigger auto-substitution by forcing a reload of the depth chart
              console.log('[PlayerManagement] Injury status changed, triggering auto-substitution check');
              // Force a reload of players to trigger the auto-substitution effect
              fetchPlayers();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RosterManagement;